import { BASE_URL } from "#next-seo.config";
import { generateNextSeo } from "next-seo/pages";
import Head from "next/head";
import Link from "next/link";
import { Fragment, useRef, useState } from "react";
import useSWR from "swr";
import BlogPost from "~/components/BlogPost";
import Container from "~/components/Container";
import Subscribe from "~/components/Subscribe";
import { apiUrl } from "~/config";
import { blogIndexHref, pageCount, POSTS_PER_PAGE } from "~/lib/blog";
import type { BlogIndexProps } from "~/lib/blog-index";
import fetcher from "~/lib/fetcher";

// Links and buttons share these, so paging looks the same whether it navigates or searches. The `!` background
// classes are for the buttons, which global.scss paints gray (see "Every <button> starts gray" in AGENTS.md).
const pageControl =
  "inline-block rounded px-3 py-1 text-base no-underline shadow-none text-gray-700 dark:text-gray-300 !bg-transparent hover:!bg-gray-100 dark:!bg-transparent dark:hover:!bg-gray-800";
const currentPage =
  "inline-block rounded px-3 py-1 text-base bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900";

function Pagination({
  page,
  totalPages,
  href,
  onPage
}: {
  page: number;
  totalPages: number;
  /** Each page's URL, when every page is a page of its own. */
  href?: (page: number) => string;
  /** Otherwise, called with the page to show. */
  onPage?: (page: number) => void;
}) {
  if (totalPages <= 1) {
    return null;
  }

  const control = (to: number, label: string, ariaLabel = `Page ${to}`) =>
    href ? (
      <Link href={href(to)} aria-label={ariaLabel} className={pageControl}>
        {label}
      </Link>
    ) : (
      <button type="button" aria-label={ariaLabel} onClick={() => onPage(to)} className={pageControl}>
        {label}
      </button>
    );

  return (
    <nav aria-label="Pagination" className="flex items-center justify-between w-full mt-4">
      <div className="w-28">{page > 1 && control(page - 1, "← Newer", "Newer posts")}</div>
      <div className="flex flex-wrap items-center justify-center gap-1">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) =>
          n === page ? (
            <span key={n} aria-current="page" className={currentPage}>
              {n}
            </span>
          ) : (
            <Fragment key={n}>{control(n, String(n))}</Fragment>
          )
        )}
      </div>
      <div className="w-28 text-right">{page < totalPages && control(page + 1, "Older →", "Older posts")}</div>
    </nav>
  );
}

function Listing({ posts, category, page, totalPosts, categories }: BlogIndexProps) {
  const [searchValue, setSearchValue] = useState("");
  const [searchPage, setSearchPage] = useState(1);
  const listTop = useRef<HTMLHeadingElement>(null);
  // Every post's views in one request, rather than one per post listed.
  const { data } = useSWR(`${apiUrl}/api/blog/views`, fetcher);
  const views: Record<string, number> | null = data?.views ?? null;

  const current = categories.find(({ slug }) => slug === category);
  const title = `${current ? `${current.name} – ` : ""}Samuel Batista's Blog${page > 1 ? ` – Page ${page}` : ""}`;
  const description = current
    ? current.description
    : "Samuel Batista's articles on how AI models work, setting up PCs, and whatever else is on his mind.";
  const url = `${BASE_URL}${blogIndexHref(category, page)}`;

  // Search looks through the whole listing, not just this page, and pages its matches in place.
  const searching = searchValue !== "";
  const matches = searching
    ? posts.filter(({ title }) => title.toLowerCase().includes(searchValue.toLowerCase()))
    : posts;
  const shownPage = searching ? searchPage : page;
  const shown = matches.slice((shownPage - 1) * POSTS_PER_PAGE, shownPage * POSTS_PER_PAGE);

  return (
    <Container>
      <Head>
        {generateNextSeo({
          title,
          description,
          canonical: url,
          openGraph: {
            url,
            title,
            description
          }
        })}
      </Head>
      <div className="flex flex-col justify-center items-start max-w-2xl mx-auto mb-16">
        <h1 className="font-bold text-3xl md:text-5xl tracking-tight mb-4 text-black dark:text-white">Blog</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {`In total, I've written ${totalPosts} articles on this site.
            Use the search by title below, or pick a category.`}
        </p>
        <div className="relative w-full mb-4">
          <input
            aria-label={current ? `Search ${current.name} articles` : "Search articles"}
            type="text"
            autoFocus
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setSearchPage(1);
            }}
            placeholder={current ? `Search ${current.name} articles` : "Search articles"}
            className="px-4 py-2 border border-gray-300 dark:border-gray-900 focus:ring-blue-500 focus:border-blue-500 block w-full rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
          <svg
            className="absolute right-3 top-3 h-5 w-5 text-gray-400 dark:text-gray-300"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <Subscribe />
        <h3
          ref={listTop}
          className="scroll-mt-28 font-bold text-2xl md:text-4xl tracking-tight mb-4 mt-4 text-black dark:text-white"
        >
          {current ? current.name : "All Posts"}
        </h3>
        <nav aria-label="Categories" className="flex flex-wrap gap-2 mb-8">
          {[{ slug: null, name: "All", count: totalPosts }, ...categories].map(({ slug, name, count }) => (
            <Link
              key={name}
              href={blogIndexHref(slug)}
              aria-current={slug === category ? "true" : undefined}
              className={`rounded-full border px-3 py-1 text-sm no-underline ${
                slug === category
                  ? "border-gray-900 bg-gray-900 text-white hover:text-white dark:border-gray-100 dark:bg-gray-100 dark:text-gray-900 dark:hover:text-gray-900"
                  : "border-gray-300 text-gray-700 hover:border-blue-500 dark:border-gray-700 dark:text-gray-300 dark:hover:border-blue-500"
              }`}
            >
              {name}
              <span className="ml-1.5 opacity-60">{count}</span>
            </Link>
          ))}
        </nav>
        {!shown.length && "No posts found."}
        {shown.map((frontMatter) => (
          <BlogPost
            key={`${frontMatter.isDraft ? "draft" : "post"}:${frontMatter.slug}`}
            {...frontMatter}
            views={views ? (views[`/blog/${frontMatter.slug}`] ?? 0) : null}
            fetchViews={false}
          />
        ))}
        {searching ? (
          <Pagination
            page={searchPage}
            totalPages={pageCount(matches.length)}
            onPage={(to) => {
              setSearchPage(to);
              listTop.current?.scrollIntoView();
            }}
          />
        ) : (
          <Pagination page={page} totalPages={pageCount(posts.length)} href={(to) => blogIndexHref(category, to)} />
        )}
      </div>
    </Container>
  );
}

export default function BlogIndex(props: BlogIndexProps) {
  // Next keeps a page's component mounted when navigating between two URLs of the same route, from one category
  // to another, say. Keying on the page makes every page start with a clear search, whichever route it is on.
  return <Listing key={blogIndexHref(props.category, props.page)} {...props} />;
}
